// Where the reader was on the charts page, remembered across a trip to another mode.
//
// Not a scroll offset. A mode switch rebuilds the question tree and every chart above the reader
// comes back as a placeholder, so a remembered offset lands somewhere else and needs a total-height
// oracle to be corrected. What is remembered here is an element and how far into it the reader was
// looking; on return that element is measured where it now is, which is what makes the heights of
// everything above it irrelevant.
//
// The same pair, re-run for as long as the page keeps moving instead of once, is what holds the
// reader still while a change re-lays out the questions above them without leaving the charts page.

// the dom ids a question and its batch rows carry, written by page_lib questionElement /
// batchElement and by batchElementId on the React side. Item binds greedily so a question key
// that itself contains a literal "-b-<digits>" still splits at the last one, not the first.
const BATCH_ANCHOR_ID = /^q-(.+)-b-(\d+)$/
const QUESTION_ANCHOR_ID = /^q-(.+)$/

// the line the reader is taken to be reading at, and the floor for any line the page offers
export const DEFAULT_ANCHOR_LINE = 70

// how long the page has to stay still before the guard lets go, in ms. Not a budget for the whole
// hold: a reset swaps every chart for a placeholder and back, and the charts return one at a time
// over a stretch no fixed window covers, so movement refreshes this rather than the clock spending it.
const HOLD_QUIET = 2000

// an upper bound on one hold, so a surface that never stops resizing cannot own the page forever
const HOLD_LIMIT = 30_000

// a scroll the guard did not perform, beyond this many pixels, is the reader taking the page back
const HOLD_TAKEOVER = 2

// the reader moving the page themselves, by whichever means, outranks the guard immediately —
// waiting for the scroll event to report it leaves one frame in which the guard fights them
const HOLD_TAKEOVER_EVENTS = ['wheel', 'touchmove', 'keydown']

// QUESTION_ANCHOR_ID alone decides this: every batch-shaped id also matches the plain pattern.
const isAnchorId = id => QUESTION_ANCHOR_ID.test(id || '')

// the question an anchor hangs off, for a batch row the rebuilt report no longer draws
const questionIdOf = id => {
  const batch = BATCH_ANCHOR_ID.exec(id || '')
  if (batch) return `q-${batch[1]}`

  return QUESTION_ANCHOR_ID.test(id || '') ? id : null
}

const scrollTopOf = () => window.scrollY || document.documentElement.scrollTop || 0

// as far down as the page can currently be scrolled. A page that shrinks under the reader — every
// chart above them going back to a placeholder — is scrolled here by the browser, not by them.
const maxScrollTopOf = () =>
  Math.max(
    0,
    (document.documentElement.scrollHeight || 0) - (window.innerHeight || document.documentElement.clientHeight || 0)
  )

// the deepest element the line crosses that the rebuilt page can be asked for by id
const anchorElementAt = line => {
  const x = Math.round((window.innerWidth || document.documentElement.clientWidth || 0) / 2)

  return (document.elementsFromPoint?.(x, line) || []).find(el => el?.id && isAnchorId(el.id)) || null
}

// A line that falls in the gap between two questions crosses nothing the page can be asked for, and
// the reader is then looking at the question about to start below it. The nearest one wins, so a
// question is chosen over its own batch rows, which start further down.
const anchorElementBelow = (line, accept = () => true) => {
  let nearest = null
  let nearestTop = Infinity

  for (const el of document.querySelectorAll?.('[id^="q-"]') || []) {
    if (!isAnchorId(el.id) || !accept(el)) continue

    const top = el.getBoundingClientRect().top
    if (line <= top && top < nearestTop) {
      nearest = el
      nearestTop = top
    }
  }

  return nearest
}

// The first question whose header the reader can see: it starts below the line and above the bottom
// of the window. A question that started above the line is only partly in view, and a chart drawing
// in it above the line would push everything the reader is looking at down with it.
const questionHeaderInView = line => {
  const bottom = window.innerHeight || document.documentElement.clientHeight || 0
  const nearest = anchorElementBelow(line, el => !BATCH_ANCHOR_ID.test(el.id))

  return nearest && nearest.getBoundingClientRect().top < bottom ? nearest : null
}

// What the reader is looking at. Null while they are at the top of the page, which restores to the
// top rather than to an element. A visible question header wins; only a question taller than the
// window, with no header in view, is held by whatever the line crosses inside it.
export const captureScrollAnchor = (anchorLine = DEFAULT_ANCHOR_LINE) => {
  const line = Math.max(DEFAULT_ANCHOR_LINE, anchorLine || 0)
  const scrolled = 0 < scrollTopOf()
  const element =
    (scrolled ? questionHeaderInView(line) : null) ||
    anchorElementAt(line) ||
    (scrolled ? anchorElementBelow(line) : null)
  if (!element) return null

  return { id: element.id, offset: Math.round(line - element.getBoundingClientRect().top), line }
}

// An element that is in the tree but has no box yet is not somewhere the reader can be put: its
// rect reads all zeros, which measures as the top of the document and throws the page there.
const laidOut = element => {
  if (!element) return false

  const rect = element.getBoundingClientRect()

  return 0 !== rect.width || 0 !== rect.height
}

// eslint-disable-next-line unicorn/prefer-query-selector -- ids carry arbitrary question keys, which querySelector would need escaped
const byId = id => document.getElementById(id)

// the anchored element, or the question a batch row hangs off once the rebuilt report stops drawing it
const anchorElementOf = id => [byId(id), byId(questionIdOf(id))].find(el => laidOut(el)) || null

// Puts the anchored element back under the line it was captured at, measuring it where it now is.
// Answers whether it could, so the caller can fall back to the top of the page.
export const restoreScrollAnchor = anchor => {
  if (!anchor?.id) return false

  const element = anchorElementOf(anchor.id)
  if (!element) return false

  const rect = element.getBoundingClientRect()
  const documentTop = rect.top + scrollTopOf()
  const line = null == anchor.line ? DEFAULT_ANCHOR_LINE : anchor.line

  // A question that came back shorter than the depth the reader was at no longer has that depth, and
  // keeping it would put them on whatever follows -- a view closed on a tall question lands them far
  // below it. They stay on the question instead: its end on screen, or all of it once it fits. The
  // anchor itself is kept, so a question that only shrank to its placeholder gets its depth back.
  const offset =
    Number.isFinite(rect.height) && 0 < rect.height && anchor.offset > rect.height
      ? Math.max(0, rect.height - (window.innerHeight - line))
      : anchor.offset

  window.scrollTo(0, Math.max(0, Math.round(documentTop - line + offset)))

  return true
}

// The page is not allowed to get shorter while the guard has the reader. A report whose charts have
// all gone back to placeholders is a fraction of its own height, and the browser answers that by
// scrolling to the new end — the jump to the bottom, which no correction can undo because the
// position the guard wants has stopped existing. Holding the floor at the height the reader arrived
// with keeps every intermediate layout inside the page they were already on.
const floorPageHeight = () => {
  const body = document.body
  if (!body) return () => {}

  const height = document.documentElement.scrollHeight || 0
  const restore = body.style.minHeight

  body.style.minHeight = `${height}px`

  return () => {
    body.style.minHeight = restore
  }
}

let hold = null

// What the page is still waiting for. A refetch leaves the questions it asked for as placeholders
// for as long as the answer takes, and nothing moves meanwhile -- a quiet page, but not a settled
// one. While anything here is outstanding the guard does not count the quiet; the cap still holds.
const pending = new Set()

// Keeps any hold, this one or the next, from letting go before the promise settles -- or before the
// cap, since a fetch that fails may never answer, and waiting on it for good would leave every later
// hold to run to the cap too.
export const holdUntil = promise => {
  if (!promise?.then) return

  pending.add(promise)

  const settle = () => pending.delete(promise)
  promise.then(settle, settle)
  setTimeout(settle, HOLD_LIMIT)
}

const stopHold = settled => {
  if (!hold) return

  const { anchor, frame, onScroll, onTakeover, dropFloor } = hold
  hold = null

  cancelAnimationFrame(frame)
  window.removeEventListener('scroll', onScroll)
  for (const event of HOLD_TAKEOVER_EVENTS) window.removeEventListener(event, onTakeover)

  dropFloor()

  // the floor coming off can shorten the page under them one last time, so they land after it
  if (settled) restoreScrollAnchor(anchor)
}

// A deliberate scroll — a jump to a question, a mode switch putting the reader back on its own
// anchor — outranks the guard, so a caller about to scroll on purpose lets it go first.
export const releaseScrollAnchor = () => stopHold(false)

// how long the reader has to have stopped scrolling before the place they stopped at is held, in ms
const FOLLOW_SCROLL_END = 150

// how often a follower checks whether the reader is held, in ms
const FOLLOW_TICK = 250

let follow = null

// While the report is still arriving, a reader who scrolls is held on whatever they stop at. The
// browser's own scroll anchoring does not cover this: it stands down whenever the block the reader
// is looking at changes height, and every block does as it goes to its loading state and comes back
// drawn. Scrolling ends a hold at once, as it always does; stopping picks the new place up, and a
// hold that ran into its cap is picked up again where it left the reader. Ends when the promise
// settles, the last hold then letting go once the page has stayed still. Holds nothing while `active`
// says the page is showing something else.
export const followReaderUntil = (promise, { active = () => true } = {}) => {
  if (!promise?.then) return

  follow?.stop()
  holdUntil(promise)

  let scrolledAt = 0
  const onScroll = () => {
    // the guard's own corrections are not the reader moving
    if (!hold) scrolledAt = performance.now()
  }

  const timer = setInterval(() => {
    if (!active()) return releaseScrollAnchor()
    if (!hold && FOLLOW_SCROLL_END < performance.now() - scrolledAt) holdScrollAnchor()
  }, FOLLOW_TICK)

  const current = {
    stop: () => {
      clearInterval(timer)
      window.removeEventListener('scroll', onScroll)
      if (follow === current) follow = null
    }
  }

  follow = current
  window.addEventListener('scroll', onScroll, { passive: true })
  promise.then(current.stop, current.stop)
}

// Holds the reader on what they are looking at while a change re-lays out what is above them.
// Nothing above the line is measured: the anchored element is found where it now is on every
// frame, so a question that came back taller is already accounted for.
//
// The hold lasts as long as the page keeps moving under the reader rather than for a set time. A
// reset takes every chart down to a placeholder and brings them back one by one, and a clock the
// guard started before any of that cannot know when the last one lands.
//
// Takes a remembered anchor to be put back and then held, or captures where the reader is now.
// Answers whether it has them, so a caller with an anchor that no longer names anything can fall
// back to the top of the page.
export const holdScrollAnchor = ({ anchor, anchorLine, quiet = HOLD_QUIET } = {}) => {
  releaseScrollAnchor()

  const held = anchor || captureScrollAnchor(anchorLine)
  if (!held) return false

  // the floor goes down before anything is measured, so no intermediate layout can be too short
  // for the position the guard is about to ask for
  const dropFloor = floorPageHeight()

  if (anchor && !restoreScrollAnchor(anchor)) {
    dropFloor()
    return false
  }

  const now = performance.now()
  const current = {
    anchor: held,
    frame: null,
    dropFloor,
    expected: scrollTopOf(),
    quietUntil: now + quiet,
    limit: now + HOLD_LIMIT
  }

  current.onTakeover = () => releaseScrollAnchor()
  current.onScroll = () => {
    const top = scrollTopOf()
    // the page shrinking under the reader scrolls itself to its own new end; that is not them
    if (top === current.expected || top >= maxScrollTopOf()) return

    if (HOLD_TAKEOVER < Math.abs(top - current.expected)) releaseScrollAnchor()
  }

  const tick = () => {
    const at = performance.now()
    if (current.limit < at) return stopHold(true)
    if (pending.size) current.quietUntil = at + quiet
    else if (current.quietUntil < at) return stopHold(true)

    if (restoreScrollAnchor(current.anchor)) {
      const corrected = scrollTopOf()

      // a correction that had somewhere to go means the page is still settling — wait it out again
      if (corrected !== current.expected) current.quietUntil = at + quiet

      current.expected = corrected
    }

    current.frame = requestAnimationFrame(tick)
  }

  hold = current

  window.addEventListener('scroll', current.onScroll, { passive: true })
  for (const event of HOLD_TAKEOVER_EVENTS) window.addEventListener(event, current.onTakeover, { passive: true })

  current.frame = requestAnimationFrame(tick)

  return true
}
